import type {
  CommandSpec,
  ControlCatalog,
  ControlClientInfo,
  InstalledWshubApp,
  DeviceFsEntry,
  DeviceInfo,
  FileMeta,
  DeviceFileKind,
  DeviceRecord,
  CurrentUser,
  PairingSession,
  RegistryEntry,
  RegistryVersionsResponse,
  RegistryVersionDiff,
  RegistryDuplicateGroup,
  RegistryHistoryResponse,
  RegistryDriftResponse,
  McpTool,
} from './types';
import { API_BASE, API_CREDENTIALS, IS_SAME_ORIGIN } from './apiBase';

const TOKEN_KEY = 'adh.token';

/**
 * Browser custom event fired by {@link jsonFetch} when the server returns a
 * 401 from a gated endpoint. As of Phase 2.3 the SPA uses the auto-issued
 * client-id cookie path so 401s only happen when a custom `?token=` /
 * `x-adh-token` is wrong; AuthGate listens and re-fetches `/auth/me`
 * which clears the bad state. The event name is namespaced so it
 * doesn't collide with the vendored libraries (lucide, react-query, …).
 */
export const UNAUTHORIZED_EVENT = 'adh:unauthorized';

/**
 * Legacy shared bearer token stored in localStorage. The Phase 0 cookie
 * path replaces this for human users; the helpers remain so power-users
 * with a CLI token can still drive the UI from another tab.
 */
export function getToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const v = localStorage.getItem(TOKEN_KEY);
  return v && v.length > 0 ? v : undefined;
}

export function setToken(v: string | undefined): void {
  if (typeof window === 'undefined') return;
  if (!v) localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, v);
}

function dispatchUnauthorized(detail: { path: string }): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail }));
  } catch {
    /* dispatch failures are non-fatal */
  }
}

export function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  // Same-origin (default PWA at localhost:10987): construct against the
  // page origin, then strip it — paths stay relative so the existing
  // service-worker / vite-proxy / cookie behaviour is unchanged.
  //
  // Cross-origin (SPA hosted on a public hub like https://hub.example.com,
  // but the local agent is still at http://127.0.0.1:10987): construct
  // against the local agent's origin and DO NOT strip — fetch() must hit
  // the absolute URL, otherwise the request goes back to the public hub
  // which has no /api routes.
  const baseOrigin = IS_SAME_ORIGIN ? window.location.origin : API_BASE;
  const url = new URL(path, baseOrigin);
  const token = getToken();
  if (token) url.searchParams.set('token', token);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  if (IS_SAME_ORIGIN) return url.toString().replace(window.location.origin, '');
  return url.toString();
}

async function jsonFetch<T>(input: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('x-adh-token', token);
  if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData) && !(init.body instanceof Blob)) {
    headers.set('Content-Type', 'application/json');
  }
  // Credentials policy:
  //   - Same-origin (PWA at localhost:10987 / vite dev with proxy): keep
  //     `include` so the `adh_user` session cookie round-trips on every
  //     fetch — existing behaviour, no regression for current users.
  //   - Cross-origin (SPA on https://hub.example.com → http://127.0.0.1:10987):
  //     `omit`, because browsers refuse `Allow-Origin: *` + `Allow-Credentials:
  //     true`. Auth on this path will switch to the header-only token model
  //     when the production CORS tightening + admin-token middleware lands.
  const res = await fetch(input, { credentials: API_CREDENTIALS, ...init, headers });
  const text = await res.text();
  let data: unknown = undefined;
  if (text.length > 0) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    // 401 fires the global unauthorized event so the router can redirect
    // to /login without every caller having to handle it. Whitelist
    // `/api/auth/*` so the login form itself doesn't trip the redirect
    // when displaying a bad-credentials toast.
    if (res.status === 401 && !input.includes('/api/auth/')) {
      dispatchUnauthorized({ path: input });
    }
    const obj = (typeof data === 'object' && data !== null ? data : {}) as { error?: unknown; detail?: unknown };
    const detail =
      typeof obj.error === 'string'
        ? `${obj.error}${typeof obj.detail === 'string' ? ': ' + obj.detail : ''}`
        : `HTTP ${res.status}`;
    const err = new Error(detail) as Error & { status?: number; data?: unknown };
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data as T;
}

/* ---------------- Auth ---------------- */
export const authApi = {
  /** Returns the current user (or null when not signed in). Always 200. */
  me(): Promise<{ user: CurrentUser | null }> {
    return jsonFetch(buildUrl('/api/auth/me'));
  },
  login(username: string): Promise<{ user: CurrentUser }> {
    return jsonFetch(buildUrl('/api/auth/login'), {
      method: 'POST',
      body: JSON.stringify({ username }),
    });
  },
  logout(): Promise<{ ok: true }> {
    return jsonFetch(buildUrl('/api/auth/logout'), { method: 'POST' });
  },
};

/* ---------------- Client config ---------------- */

/**
 * Shape returned by GET /api/config/client. Currently a single UI knob
 * (sidebar session cap); grows lazily as more user-tunable values move
 * from hard-coded constants into adh.defaults.json.
 */
export interface ClientConfig {
  ui: {
    maxActiveSessions: number;
  };
}

export const configApi = {
  client(): Promise<ClientConfig> {
    return jsonFetch(buildUrl('/api/config/client'));
  },
};

/* ---------------- Devices ---------------- */

/** Shape returned by GET /api/devices — registry record merged with adb live state. */
export type DeviceListEntry = DeviceRecord & {
  state: string;
  model?: string;
  product?: string;
  device?: string;
  transportId?: string;
  /** Present for admin-only "adb sees this but registry hasn't reconciled yet" rows. */
  unregistered?: boolean;
  /**
   * Tiered-transport (May 2026): `state` is `'offline'` for ADB but
   * a tcpip-mirror transport IS in `adb devices`. Surfaced as the
   * 「仅镜像备线」 orange badge in the device card so the user knows
   * scrcpy still works while other ADB commands will 503 / fail.
   */
  mirrorOnly?: boolean;
  /**
   * Runtime hint that the wireless-debug backup is not yet
   * provisioned for this USB-online device — surfaced in the topbar
   * `WirelessPairBanner` so the user knows to enable wireless
   * debugging by hand.
   */
  needsWirelessPair?: boolean;
};

export const devicesApi = {
  list(refresh = false): Promise<{ devices: DeviceListEntry[] }> {
    return jsonFetch(buildUrl('/api/devices', refresh ? { refresh: '1' } : undefined));
  },
  /** Single-device fetch used by `DeviceDetail` to load record metadata. */
  get(serial: string): Promise<{ device: DeviceListEntry }> {
    return jsonFetch(buildUrl(`/api/devices/${encodeURIComponent(serial)}`));
  },
  /**
   * Recover from a stuck adb daemon. Server runs
   * `adb kill-server && adb start-server` then re-lists. ALL active mirror
   * / logcat / control sessions get torn down — callers should be ready to
   * re-acquire after the promise resolves.
   */
  restartAdb(): Promise<{ ok: true; devices: DeviceInfo[] }> {
    return jsonFetch(buildUrl('/api/devices/restart-adb'), { method: 'POST' });
  },
  forget(serial: string): Promise<{ ok: true; existed: boolean }> {
    return jsonFetch(buildUrl(`/api/devices/${encodeURIComponent(serial)}`), { method: 'DELETE' });
  },
  /**
   * Force an immediate `adb connect <endpoint>` for a wireless device.
   * Use when the user enters a detail page and the device is offline —
   * the periodic watchdog would otherwise wait up to 30 s. Returns
   * 400 INVALID_ARGS for USB / agent devices.
   */
  reconnect(serial: string): Promise<{ ok: true; endpoint: { host: string; port: number }; output: string }> {
    return jsonFetch(buildUrl(`/api/devices/${encodeURIComponent(serial)}/reconnect`), {
      method: 'POST',
    });
  },
  /**
   * Phone-side network info — Wi-Fi SSID (best-effort) + IPv4 / CIDR
   * for each non-loopback interface. Used by the wireless-pair modal
   * to flag cross-subnet mismatches before the user wastes time
   * scanning a QR that mDNS will never see.
   */
  network(serial: string): Promise<DeviceNetworkInfo> {
    return jsonFetch(buildUrl(`/api/devices/${encodeURIComponent(serial)}/network`));
  },
};

/* ------------------------- Phase 4 — agents ------------------------- */

export interface AgentSummary {
  agentId: string;
  name?: string;
  userId: string;
  createdAt: number;
  expiresAt: number;
  revokedAt?: number;
  online: boolean;
  hostname?: string;
  os?: 'darwin' | 'linux' | 'win32';
  agentVersion?: string;
  deviceCount: number;
}

export interface MintedAgentToken {
  agentId: string;
  jti: string;
  /** Raw JWT — returned ONCE; show + copy and let the user save it. */
  token: string;
  expiresAt: number;
}

export const agentApi = {
  list(): Promise<{ agents: AgentSummary[] }> {
    return jsonFetch(buildUrl('/api/agent/list'));
  },
  mint(opts: { name?: string; ttlSec?: number } = {}): Promise<MintedAgentToken> {
    return jsonFetch(buildUrl('/api/agent/tokens'), {
      method: 'POST',
      body: JSON.stringify(opts),
    });
  },
  revoke(agentId: string): Promise<{ ok: true }> {
    return jsonFetch(buildUrl(`/api/agent/${encodeURIComponent(agentId)}`), {
      method: 'DELETE',
    });
  },
};

/* ---------------------- Phase 5 — batch ops ---------------------- */

/** Mirrors `src/server/batch.ts#BatchAction`. */
export type BatchAction =
  | { kind: 'install'; apkPath: string; flags?: string[] }
  | { kind: 'cmd'; name: string; args: Record<string, unknown> }
  | { kind: 'shell'; command: string };

export type BatchJobStatus = 'pending' | 'running' | 'done' | 'cancelled';
export type SerialStatus =
  | 'pending'
  | 'running'
  | 'success'
  | 'failed'
  | 'skipped'
  | 'cancelled';

export interface SerialResult {
  serial: string;
  status: SerialStatus;
  startedAt?: number;
  finishedAt?: number;
  error?: string;
  skipReason?: 'forbidden' | 'offline';
  /** Action-specific payload (e.g. `{stdout,stderr}` for install). */
  result?: unknown;
}

export interface BatchJob {
  jobId: string;
  action: BatchAction;
  userId: string;
  status: BatchJobStatus;
  concurrency: number;
  createdAt: number;
  finishedAt?: number;
  serials: SerialResult[];
}

export type BatchEvent =
  | { type: 'snapshot'; job: BatchJob }
  | { type: 'job'; job: BatchJob }
  | { type: 'serial'; jobId: string; result: SerialResult };

export const batchApi = {
  /**
   * Stream the APK bytes as application/octet-stream (no multipart).
   * `serials` is a comma-separated list passed as a query param so we
   * don't have to negotiate a multipart parser on the server.
   */
  async install(opts: {
    apk: File | Blob;
    apkName: string;
    serials: string[];
    flags?: string[];
    concurrency?: number;
  }): Promise<{ jobId: string; job: BatchJob }> {
    // Use `buildUrl` directly so query-string params + token injection
    // go through the same code path as every other API helper. The
    // previous implementation wrapped the relative path returned by
    // `buildUrl` in `new URL()` without a base, which threw
    // "Failed to construct 'URL': Invalid URL" because the WHATWG URL
    // parser requires an absolute URL or an explicit base.
    const url = buildUrl('/api/batch/install', {
      filename: opts.apkName,
      serials: opts.serials.join(','),
      flags: opts.flags?.length ? opts.flags.join(',') : undefined,
      concurrency: opts.concurrency,
    });
    const buf = await opts.apk.arrayBuffer();
    const res = await fetch(url, {
      method: 'POST',
      credentials: API_CREDENTIALS,
      headers: { 'Content-Type': 'application/octet-stream' },
      body: buf,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`batch install failed: ${res.status} ${text}`);
    }
    return res.json();
  },

  cmd(input: {
    serials: string[];
    name: string;
    args?: Record<string, unknown>;
    concurrency?: number;
  }): Promise<{ jobId: string; job: BatchJob }> {
    return jsonFetch(buildUrl('/api/batch/cmd'), {
      method: 'POST',
      body: JSON.stringify({ args: {}, ...input }),
    });
  },

  shell(input: {
    serials: string[];
    command: string;
    concurrency?: number;
  }): Promise<{ jobId: string; job: BatchJob }> {
    return jsonFetch(buildUrl('/api/batch/shell'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  job(jobId: string): Promise<{ job: BatchJob }> {
    return jsonFetch(buildUrl(`/api/batch/jobs/${encodeURIComponent(jobId)}`));
  },

  cancel(jobId: string): Promise<{ ok: boolean; job: BatchJob | undefined }> {
    return jsonFetch(buildUrl(`/api/batch/jobs/${encodeURIComponent(jobId)}/cancel`), {
      method: 'POST',
    });
  },

  manifest(opts: { scope?: 'mine' | 'public' | 'all'; format?: 'json' | 'env' | 'txt' } = {}): string {
    return buildUrl('/api/devices/manifest', {
      scope: opts.scope ?? 'mine',
      format: opts.format ?? 'json',
    });
  },
};

/** Hub-side network info. Mirrors `src/server/network.ts#LocalNetwork`. */
export interface LocalNetworkInfo {
  interfaces: Array<{
    name: string;
    address: string;
    cidr: string;
    netmask: string;
    ssid?: string;
    kind: 'wifi' | 'ethernet' | 'other';
  }>;
  primary?: LocalNetworkInfo['interfaces'][number];
}

/** Phone-side network info. Mirrors the GET /api/devices/:serial/network response. */
export interface DeviceNetworkInfo {
  ssid?: string;
  primary: { iface: string; ipv4: string; cidr: string } | null;
  interfaces: Array<{ iface: string; ipv4: string; cidr: string }>;
}

/* ---------------- Wireless pairing (Phase 1) ---------------- */

export const wirelessApi = {
  /**
   * Begin a pairing session. `launchOnSerial` tells the server to try
   * popping the QR-pair Settings activity on a USB-connected device so
   * the user doesn't have to navigate manually; safe to omit when the
   * user already has the wireless-debugging page open.
   */
  startPair(input: { launchOnSerial?: string; ttlMs?: number } = {}): Promise<{ session: PairingSession }> {
    return jsonFetch(buildUrl('/api/wireless/pair/start'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /** Poll the session. Caller stops polling once `state` becomes terminal. */
  status(sessionId: string): Promise<{ session: PairingSession }> {
    return jsonFetch(buildUrl('/api/wireless/pair/status', { sessionId }));
  },
  /**
   * Manual pair fallback for environments where mDNS is silent (macOS
   * multi-NIC, corporate Wi-Fi client isolation). The user reads
   * `host:port` + 6-digit code straight off the phone's
   * 「无线调试 → 通过配对码配对设备」 screen. Returns a session in
   * terminal state (`connected` / `failed`) so the existing modal
   * polling loop renders the result without a parallel UI path.
   */
  /**
   * Manual pairing for environments where mDNS doesn't reach the hub.
   *
   * `port` is the **pair port** (ephemeral, shown next to "配对码"
   * on the phone). `connectPort` is the optional **connect port**
   * (persistent, shown next to "IP 与端口" on the 无线调试 main
   * screen) — supply it when you can read it off the phone to skip
   * mDNS discovery and dial it directly. When omitted, the server
   * watches mDNS for ~8s before falling back to the pair port (which
   * will fail clearly so the user can retry with the connect port).
   */
  pairManual(input: {
    host: string;
    port: number;
    connectPort?: number;
    code: string;
  }): Promise<{ session: PairingSession }> {
    return jsonFetch(buildUrl('/api/wireless/pair/manual'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  cancel(sessionId: string): Promise<{ ok: true; cancelled: boolean }> {
    return jsonFetch(buildUrl('/api/wireless/pair/cancel'), {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  },
  /**
   * One-shot mDNS reachability probe. Returns `alive: false` when no
   * `_adb-tls-pairing._tcp` advert reaches the hub within `windowMs`
   * (default 3 s). Used by the add-device modal to pre-flight the
   * QR-pair flow: when the LAN blocks multicast (corporate Wi-Fi
   * client-isolation / VPN default-route stealing the multicast
   * group), we surface an amber callout pointing the user at
   * 「手动输入」 instead of letting them stare at a doomed QR.
   */
  probeMdns(windowMs?: number): Promise<{
    alive: boolean;
    windowMs: number;
    sawAtMs: number | null;
    knownServices: number;
  }> {
    const params = windowMs ? { windowMs } : undefined;
    return jsonFetch(buildUrl('/api/wireless/mdns-probe', params));
  },
  /**
   * Drop a wireless link by serial. `forget` defaults to true on the
   * server, which also clears the registry record so the watchdog won't
   * reconnect. Pass `forget: false` to keep the record (rare).
   */
  disconnect(serial: string, forget = true): Promise<{ ok: true }> {
    return jsonFetch(buildUrl('/api/wireless/disconnect'), {
      method: 'POST',
      body: JSON.stringify({ serial, forget }),
    });
  },
  /**
   * Read the current state of the USB → Wi-Fi auto-switch toggle. Any
   * signed-in user can call this (the SPA renders the indicator even
   * for non-admins).
   */
  getAutoswitch(): Promise<{ enabled: boolean; configFile: string | null }> {
    return jsonFetch(buildUrl('/api/wireless/autoswitch'));
  },
  /**
   * Flip the auto-switch toggle. Any signed-in user can call this —
   * the Topbar exposes it next to the theme switch so a teammate can
   * pause auto-switching without chasing an admin. `persisted=false`
   * means the toggle lives in memory but the file write failed —
   * surface the error so the caller can warn the operator and retry.
   */
  setAutoswitch(enabled: boolean): Promise<{
    enabled: boolean;
    configFile: string | null;
    persisted: boolean;
    persistError?: string;
  }> {
    return jsonFetch(buildUrl('/api/wireless/autoswitch'), {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    });
  },
};

/* ---------------- Server info / settings ---------------- */
export interface ServerInfo {
  /** Currently active devices root (after `~` expansion). */
  devicesRoot: string;
  /** Hardcoded fallback (`<home>/AndroidDeviceHub/devices`). UI uses it as a hint. */
  defaultDevicesRoot: string;
  /** Path to the `adh.config.json` the server loaded; null = none discovered. */
  configFile: string | null;
  /** User home directory — useful when the UI wants to render `~/...` paths. */
  homeDir: string;
  /** `'darwin' | 'linux' | 'win32' | ...` — purely informational. */
  platform: string;
  /**
   * Pre-`AndroidDeviceHub/` builds wrote agent state into `~/.adh`. When
   * this is true the dir still exists on disk and the user hasn't yet
   * merged it into `~/AndroidDeviceHub/`. The SPA renders an amber
   * banner asking the user to migrate it manually.
   */
  legacyDataDirExists: boolean;
  /**
   * Same idea as `legacyDataDirExists` but for device files: pre-migration
   * builds dumped APKs / screenshots / logcat under
   * `~/Downloads/deviceManager`.
   */
  legacyDevicesDirExists: boolean;
  /**
   * Developer mode (ADH_DEV_MODE=1) — when true the hub is running the
   * fake-device test harness instead of real `adb devices`. The SPA
   * topbar renders a "开发者模式" badge and warns the user that
   * adb-backed operations (mirror, install, logcat) will fail.
   */
  devMode: boolean;
  /**
   * The fake serials injected by ADH_FAKE_DEVICES when devMode is on.
   * Empty array when devMode is false. Surfaced for the topbar badge
   * hover so users can see exactly which serials are synthetic.
   */
  fakeSerials: string[];
}

export const serverInfoApi = {
  get(): Promise<ServerInfo> {
    return jsonFetch(buildUrl('/api/server-info'));
  },
};

// 阶段 A：旧 `settingsApi.setCapturesDir` 已下线。devices.dir 现走 PUT
// /api/agent/config（C 类 agent-local.json）；前端用 `useAgentConfigStore`
// 直接发起改动。

/* ---------------- Host tools (apksigner / aapt2 / jadx / ...) ---------------- */
export type HostToolName =
  | 'apksigner'
  | 'aapt2'
  | 'aapt'
  | 'apkanalyzer'
  | 'bundletool'
  | 'jadx'
  | 'apktool'
  | 'readelf'
  | 'nm'
  | 'objdump'
  | 'strings'
  | 'ffprobe';

export type HostToolCategory = 'android-sdk' | 'decompile' | 'binutils' | 'media';

export type HostToolResolveSource = 'config' | 'env' | 'sdk' | 'path' | 'none';

export interface HostToolStatus {
  name: HostToolName;
  label: string;
  description: string;
  category: HostToolCategory;
  envNames: string[];
  available: boolean;
  resolvedPath?: string;
  source: HostToolResolveSource;
  configuredPath?: string;
  configuredFrom?: 'config' | 'env';
  triedPaths: string[];
  sdkRoots: string[];
  version?: string;
  error?: string;
}

export interface HostToolsResponse {
  androidSdkRoot: string | null;
  sdkAutodetect: Array<{ path: string; exists: boolean; configured: boolean }>;
  tools: HostToolStatus[];
  /** Present on POST responses only. */
  configFile?: string | null;
  persisted?: boolean;
  persistError?: string;
}

export interface HostToolsShellSnippets {
  shell: string;
  lines: string[];
  doc: string;
}

export const hostToolsApi = {
  /** Probe every registered tool. Cheap; safe to refetch on every settings-page open. */
  get(): Promise<HostToolsResponse> {
    return jsonFetch(buildUrl('/api/host-tools'));
  },
  /**
   * Persist `androidSdkRoot` and/or per-tool path overrides via the C-tier
   * `PUT /api/agent/config { hostTools: ... }` channel — 阶段 A 把 host-tools
   * 写路径合并到了 agent-local.json，老的 `POST /api/host-tools` 已下线。
   *
   * 返回值仍是 `HostToolsResponse` 形状（探测结果 + persisted/configFile）。
   * 我们在写完后再 GET 一次 `/api/host-tools` 以拿到 server 重新解析后的真路径。
   */
  async save(update: {
    androidSdkRoot?: string | null;
    paths?: Partial<Record<HostToolName, string | null>>;
  }): Promise<HostToolsResponse> {
    type AgentConfigPutResp = {
      configFile: string | null;
      persisted: boolean;
      persistError?: string | null;
    };
    const r = (await jsonFetch(buildUrl('/api/agent/config'), {
      method: 'PUT',
      body: JSON.stringify({ hostTools: update }),
    })) as AgentConfigPutResp;
    const probe = await this.get();
    return {
      ...probe,
      configFile: r.configFile ?? probe.configFile,
      persisted: r.persisted,
      persistError: r.persistError ?? undefined,
    };
  },
  /** Clear the resolver cache and re-probe. Same response shape as `get()`. */
  rescan(): Promise<HostToolsResponse> {
    return jsonFetch(buildUrl('/api/host-tools/scan'), { method: 'POST' });
  },
  /** Generate copy-paste env exports for the given shell. */
  shellSnippets(shell: 'zsh' | 'bash' | 'fish' | 'powershell' | 'cmd'): Promise<HostToolsShellSnippets> {
    return jsonFetch(buildUrl('/api/host-tools/shell-snippets', { shell }));
  },
};

/* ---------------- Network helpers ---------------- */
export interface ProxyCandidate {
  host: string;
  port: number;
  /** Friendly tool name based on port (`Charles`, `mitmproxy`, ...). */
  label: string;
  /** True when a 1.5s TCP probe from the server reached the (host,port). */
  alive: boolean;
}

export const networkApi = {
  /**
   * Server scans this machine's LAN IPs against well-known packet-capture
   * proxy ports (Charles 8888, mitm 8080, Whistle 8899, generic 9090) and
   * returns alive ones first. Cheap; UI can re-fetch on every modal open.
   */
  proxyCandidates(): Promise<{ candidates: ProxyCandidate[] }> {
    return jsonFetch(buildUrl('/api/network/proxy-candidates'));
  },
  /**
   * Read the hub's local IPv4 interfaces (cached briefly server-side).
   * Used by the wireless-pair modal to show "Hub network: 192.168.1.0/24
   * (en0, OfficeWiFi)" so the user can verify the phone is on the same
   * WLAN before scanning.
   */
  local(): Promise<LocalNetworkInfo> {
    return jsonFetch(buildUrl('/api/network/local'));
  },
};

/* ---------------- Local-machine file browser (xInput=file) ---------------- */
export interface LocalBrowseRoot {
  /** Stable id — e.g. "downloads" / "devices". */
  id: string;
  /** Chinese label for the SPA tabs. */
  label: string;
  /** Absolute server-side directory. */
  path: string;
}

export interface LocalBrowseEntry {
  name: string;
  /** Absolute path; what gets written back to the form field on pick. */
  path: string;
  isDir: boolean;
  size: number;
  /** ms-since-epoch. */
  mtime: number;
}

export interface LocalBrowseListing {
  /** Which root the current directory lives under. */
  root: LocalBrowseRoot;
  /** Absolute path of the directory being listed. */
  path: string;
  /** Parent path; null when `path === root.path` (already at the root). */
  parent: string | null;
  entries: LocalBrowseEntry[];
}

/** Result of the OS-native picker dispatched by `POST /api/fs/native-pick`.
 *  Mirrors `src/server/nativePicker.ts#NativePickerResult` so the SPA can
 *  render distinct UI per state (cancel = silent, unsupported = fallback
 *  to the directory modal, error = inline warning). */
export type NativePickResult =
  | { status: 'picked'; path: string }
  | { status: 'cancelled' }
  | { status: 'unsupported'; reason: string }
  | { status: 'error'; reason: string };

export const localFsApi = {
  /** Resolve the configured browse roots (existing & readable only). */
  roots(): Promise<{ roots: LocalBrowseRoot[] }> {
    return jsonFetch(buildUrl('/api/fs/local-browse/roots'));
  },
  /**
   * List children of a directory. When `any` is true the server skips the
   * whitelist-root check and lists whatever absolute path is requested —
   * used by the modal's "前往任意路径" input so users aren't penned in by
   * the default Downloads/Desktop/Documents/devices rails.
   */
  browse(p: string, opts: { any?: boolean } = {}): Promise<LocalBrowseListing> {
    return jsonFetch(
      buildUrl('/api/fs/local-browse', { path: p, any: opts.any ? '1' : undefined })
    );
  },
  /**
   * Trigger the OS-native "Open File…" dialog on the server host. For
   * the default `adh serve` (which runs on the user's own Mac) that's
   * the Finder picker users already know — far nicer than typing
   * absolute paths or navigating server-side directory trees. Returns
   * `{status:'unsupported'}` on platforms with no picker tool installed,
   * which the caller should treat as a signal to fall back to the
   * directory browser modal.
   *
   * NB: 501 is the "unsupported" signal — `jsonFetch` would coerce that
   * to a thrown Error, so we handle the request manually and pass the
   * 501 body through as a normal discriminated-union value.
   */
  async nativePick(
    opts: { title?: string; accept?: string } = {}
  ): Promise<NativePickResult> {
    const token = getToken();
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (token) headers['x-adh-token'] = token;
    const res = await fetch(buildUrl('/api/fs/native-pick'), {
      method: 'POST',
      credentials: API_CREDENTIALS,
      headers,
      body: JSON.stringify(opts),
    });
    if (res.status === 501) {
      return (await res.json()) as NativePickResult;
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return { status: 'error', reason: text || `HTTP ${res.status}` };
    }
    return (await res.json()) as NativePickResult;
  },
  /**
   * Pop the OS file manager focused on `path`. Mirrors
   * `POST /api/fs/reveal { path }` → `revealInFileManager`. Used by
   * Files page's PC pane and pull-completion toast to give users a
   * one-click "show this APK in Finder" affordance.
   *
   * Returns the same discriminated union shape as `nativePick` (minus
   * `picked`/`cancelled`), so callers can render an inline toast on
   * `error` and a "platform unsupported" hint on `unsupported`.
   */
  async reveal(
    path: string
  ): Promise<
    | { status: 'ok' }
    | { status: 'unsupported'; reason: string }
    | { status: 'error'; reason: string }
  > {
    const token = getToken();
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (token) headers['x-adh-token'] = token;
    const res = await fetch(buildUrl('/api/fs/reveal'), {
      method: 'POST',
      credentials: API_CREDENTIALS,
      headers,
      body: JSON.stringify({ path }),
    });
    if (res.status === 501) {
      return (await res.json()) as { status: 'unsupported'; reason: string };
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return { status: 'error', reason: text || `HTTP ${res.status}` };
    }
    return (await res.json()) as { status: 'ok' };
  },
};

/* ---------------- ADB catalog + run ---------------- */
export const adbApi = {
  /**
   * Fetch the full adb command catalog. Catalog content is static for
   * the life of the server process (built once at module-load time by
   * `installBuiltins`, never varies per device), so callers should
   * cache the response indefinitely and key by nothing more than the
   * literal endpoint. The legacy signature accepted a `serial`, which
   * was forwarded to the server's `toolbox.get(serial)` cache key —
   * harmless on the server but caused the SPA to refetch on every
   * device switch and show a spurious loading state for ~50 ms even
   * though the bytes never change. Dropped in May 2026 alongside the
   * `Commands.tsx` queryKey simplification.
   */
  catalog(): Promise<{ commands: CommandSpec[] }> {
    return jsonFetch(buildUrl('/api/adb/catalog'));
  },
  run<R = unknown>(input: { serial?: string; name: string; args?: Record<string, unknown> }): Promise<{ result: R }> {
    return jsonFetch(buildUrl('/api/adb/run'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

/* ---------------- Control (in-app WS) ---------------- */
export const controlApi = {
  /**
   * Snapshot of every package currently in a hello-accepted WS session
   * for this device. Drives the Control page's package picker. Returns
   * `{ clients: [] }` when no app is connected.
   */
  clients(serial: string): Promise<{ clients: ControlClientInfo[] }> {
    return jsonFetch(buildUrl('/api/control/clients', { serial }));
  },
  /**
   * Snapshot of every package on this device whose manifest carries the
   * `com.adh.wshub.sdk` marker, joined with the live `clients()` set.
   * Returns rows for apps that are installed but NOT currently
   * WS-connected (`isConnected=false`) — the SPA renders those in red so
   * RnD/QA can spot "装了但没连上" without ssh'ing into the device.
   *
   * Failure shape mirrors {@link clients}: 503 BACKOFF when the bridge is
   * cooling down, 404 when the device is gone. Hub-side this is a real
   * adb fan-out (one `dumpsys` per third-party package, capped at
   * SCAN_CONCURRENCY=8), so don't poll faster than ~30s.
   */
  installedApps(serial: string): Promise<{ apps: InstalledWshubApp[] }> {
    return jsonFetch(buildUrl('/api/control/installed-apps', { serial }));
  },
  /**
   * Fetch the device adapter's command catalog. `package` is required on
   * devices with more than one connected app — the server returns an
   * empty catalog when neither is specified and >1 app is connected, so
   * always pass it once the picker has a selection.
   */
  catalog(serial: string, pkg?: string): Promise<ControlCatalog> {
    return jsonFetch(buildUrl('/api/control/catalog', { serial, package: pkg }));
  },
  cmd<R = unknown>(input: {
    serial: string;
    package?: string;
    name: string;
    args?: Record<string, unknown>;
    timeoutMs?: number;
    /**
     * `'preview'` makes the device-side SDK run the spec's `.snapshot`
     * lambda instead of the registered handler — the reply payload is
     * a partial Args POJO the SPA uses to prefill the form. Defaults
     * to `'apply'` (the real handler) when omitted.
     */
    mode?: 'preview' | 'apply';
  }): Promise<{ reply: R; package: string }> {
    return jsonFetch(buildUrl('/api/control/cmd'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  cancel(input: {
    serial: string;
    package?: string;
    id: string;
    reason?: string;
  }): Promise<{ ok: true; package: string }> {
    return jsonFetch(buildUrl('/api/control/cancel'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /**
   * Open a long-lived subscription to a `WsSubscribeSpec`-flavoured
   * route. The returned `subId` is what the SPA matches against the
   * `payload.subId` of `evt` frames streaming over `/api/control/events`.
   * Send `/control/unsub` with the same id when the user disables it.
   */
  sub(input: {
    serial: string;
    package?: string;
    name: string;
    args?: Record<string, unknown>;
  }): Promise<{ ok: true; subId: string; package: string }> {
    return jsonFetch(buildUrl('/api/control/sub'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /**
   * Close one or more open subscriptions. Pass `subId` for the exact
   * handle the SPA stored, or `(serial, package?, name)` to close every
   * subscription opened for that name on that device (used as a
   * recovery hatch when the SPA loses its in-memory subId).
   */
  unsub(
    input:
      | { subId: string }
      | { serial: string; package?: string; name: string }
  ): Promise<{ ok: true; closed: number }> {
    return jsonFetch(buildUrl('/api/control/unsub'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
};

/**
 * GET /api/control/registry — realtime aggregation of every connected
 * (serial × package)'s catalog flattened to one row per command. The
 * server applies the filter knobs (`q` is case-insensitive substring
 * against name / displayName / desc); the SPA just renders.
 */
export const registryApi = {
  list(input: {
    q?: string;
    serial?: string;
    package?: string;
    namespace?: string;
  } = {}): Promise<{ entries: RegistryEntry[] }> {
    return jsonFetch(buildUrl('/api/control/registry', input));
  },

  /**
   * Distinct app versions seen for a package, plus a compact summary of
   * the most recent snapshot. Backed by the persistent {@link RegistryStore}
   * — `null` `latest` means we've never seen this package boot up.
   */
  versions(pkg: string): Promise<RegistryVersionsResponse> {
    return jsonFetch(
      buildUrl('/api/control/registry/versions', { package: pkg })
    );
  },

  /** Diff two known versions of a package's command catalog. */
  diff(input: { package: string; from: string; to: string }): Promise<RegistryVersionDiff> {
    return jsonFetch(buildUrl('/api/control/registry/diff', input));
  },

  /**
   * Command names registered by more than one package. Default is
   * mismatch-only — pass `identicalToo:true` to include matched-metadata
   * collisions too.
   */
  duplicates(
    input: { identicalToo?: boolean } = {}
  ): Promise<{ groups: RegistryDuplicateGroup[] }> {
    return jsonFetch(
      buildUrl('/api/control/registry/duplicates', {
        identicalToo: input.identicalToo ? '1' : undefined,
      })
    );
  },

  /** Every snapshot we have for a package, newest last. */
  history(pkg: string): Promise<RegistryHistoryResponse> {
    return jsonFetch(buildUrl('/api/control/registry/history', { package: pkg }));
  },

  /**
   * Pull the drift report for `(serial, [package])`. Each connected package
   * is probed via the optional `diag.drift` cmd; packages whose
   * device adapter doesn't implement it return `supported:false` and a
   * human-readable reason.
   */
  drift(input: { serial: string; package?: string }): Promise<RegistryDriftResponse> {
    return jsonFetch(buildUrl('/api/control/registry/drift', input));
  },

  /**
   * URL to fetch the registry as Markdown — handed to `<a href>` /
   * `window.open` rather than to `fetch` because we want the browser's
   * download dialog rather than an in-memory string.
   */
  markdownUrl(filter: { package?: string; namespace?: string; download?: boolean } = {}): string {
    return buildUrl('/api/control/registry/export', {
      format: 'markdown',
      package: filter.package,
      namespace: filter.namespace,
      download: filter.download ? '1' : undefined,
    });
  },

  /** Inline JSON fetch of the MCP-style tool list. */
  mcpToolList(filter: { package?: string; namespace?: string } = {}): Promise<{ tools: McpTool[] }> {
    return jsonFetch(
      buildUrl('/api/control/registry/export', {
        format: 'mcp-tool-list',
        package: filter.package,
        namespace: filter.namespace,
      })
    );
  },
};

/* ---------------- Device files (PC-side mirror) ---------------- */
export const filesApi = {
  list(input: {
    serial: string;
    kind?: DeviceFileKind;
    sort?: 'mtime' | 'size' | 'name';
    order?: 'asc' | 'desc';
    withMeta?: boolean;
  }): Promise<{ items: FileMeta[] }> {
    return jsonFetch(buildUrl('/api/files', input));
  },
  meta(p: string): Promise<{ meta: FileMeta }> {
    return jsonFetch(buildUrl('/api/files/meta', { path: p }));
  },
  rawUrl(p: string, download = false): string {
    return buildUrl('/api/files/raw', { path: p, download: download ? '1' : undefined });
  },
  text(input: {
    path: string;
    offset?: number;
    limit?: number;
    startByte?: number;
  }): Promise<{
    totalSize: number;
    startByte: number;
    endByte: number;
    reachedEnd: boolean;
    totalLinesInRange: number;
    offset: number;
    limit: number;
    lines: string[];
  }> {
    return jsonFetch(buildUrl('/api/files/text', input));
  },
  delete(paths: string[]): Promise<{ deleted: string[]; missing: string[] }> {
    return jsonFetch(buildUrl('/api/files'), {
      method: 'DELETE',
      body: JSON.stringify({ paths }),
    });
  },
  cleanup(input: { serial?: string; kind?: DeviceFileKind; olderThanDays?: number; keepLatest?: number }) {
    return jsonFetch<{ deleted: string[] }>(buildUrl('/api/files/cleanup'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  saveLog(input: { serial: string; lines: string[]; prefix?: string }): Promise<{ meta: FileMeta }> {
    return jsonFetch(buildUrl('/api/files/save-log'), {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /**
   * Upload an opaque blob (e.g. a `MediaRecorder` recording from the floating
   * mirror) and save it inside the devices directory.
   *
   * Filename selection:
   *   - When `filename` is provided, the server preserves it verbatim
   *     (after light sanitisation) and adds ` (1)`, ` (2)`, … suffixes on
   *     collision. Use this for drag-drop installs where the source
   *     filename is what the user recognises (e.g.
   *     `Tap Tile!_1.0.5_APKPure.xapk`).
   *   - Otherwise the server builds a synthetic
   *     `<prefix>-<iso-timestamp>.<ext>` name. Use this for MediaRecorder
   *     blobs and other generated artefacts where the source has no
   *     meaningful name.
   */
  upload(input: {
    serial: string;
    kind: DeviceFileKind;
    ext: string;
    prefix?: string;
    filename?: string;
    blob: Blob;
  }): Promise<{ meta: FileMeta }> {
    return jsonFetch(
      buildUrl('/api/files/upload', {
        serial: input.serial,
        kind: input.kind,
        ext: input.ext,
        prefix: input.prefix,
        name: input.filename,
      }),
      {
        method: 'POST',
        body: input.blob,
        headers: { 'Content-Type': input.blob.type || 'application/octet-stream' },
      }
    );
  },
};

/* ---------------- Device fs (sdcard) ---------------- */
export const deviceFsApi = {
  list(serial: string, p: string): Promise<{ path: string; entries: DeviceFsEntry[] }> {
    return jsonFetch(buildUrl('/api/devicefs/list', { serial, path: p }));
  },
  mkdir(serial: string, p: string) {
    return jsonFetch(buildUrl('/api/devicefs/mkdir'), {
      method: 'POST',
      body: JSON.stringify({ serial, path: p }),
    });
  },
  move(serial: string, from: string, to: string) {
    return jsonFetch(buildUrl('/api/devicefs/move'), {
      method: 'POST',
      body: JSON.stringify({ serial, from, to }),
    });
  },
  remove(serial: string, p: string, recursive = false) {
    return jsonFetch(buildUrl('/api/devicefs/remove'), {
      method: 'POST',
      body: JSON.stringify({ serial, path: p, recursive }),
    });
  },
  stat(serial: string, p: string) {
    return jsonFetch<{ result: { type: string; size?: number; mtime?: string; permissions?: string } }>(
      buildUrl('/api/devicefs/stat', { serial, path: p })
    );
  },
  /**
   * URL that streams a device file directly via `adb exec-out cat` — does
   * NOT touch the devices dir. Use this for in-page previews so we don't
   * pollute the user's PC with copies they didn't ask for.
   */
  rawUrl(serial: string, devicePath: string, download = false): string {
    return buildUrl('/api/devicefs/raw', {
      serial,
      path: devicePath,
      download: download ? '1' : undefined,
    });
  },
};

/* ---------------- Transfer (PC <-> device) ---------------- */
export const transferApi = {
  pull(serial: string, devicePath: string, kind?: DeviceFileKind): Promise<{ meta: FileMeta }> {
    return jsonFetch(buildUrl('/api/transfer/pull', { serial, devicePath, kind }));
  },
  async push(serial: string, devicePath: string, file: Blob | ArrayBuffer): Promise<{ result: { remote: string; bytes: number } }> {
    const headers = new Headers({ 'Content-Type': 'application/octet-stream' });
    const token = getToken();
    if (token) headers.set('x-adh-token', token);
    const res = await fetch(buildUrl('/api/transfer/push', { serial, devicePath }), {
      method: 'POST',
      credentials: API_CREDENTIALS,
      headers,
      body: file,
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`push failed: ${res.status} ${text}`);
    }
    return (await res.json()) as { result: { remote: string; bytes: number } };
  },
};

/* ---------------------- Phase 6 — admin layer ---------------------- */

export interface AdminUser {
  username: string;
  role: 'admin';
  createdAt: number;
  lastLoginAt?: number;
  passwordHash: '<redacted>';
}

export interface AdminAgentSummary {
  agentId: string;
  bindUserId: string;
  name?: string;
  hostname?: string;
  os?: 'darwin' | 'linux' | 'win32';
  agentVersion?: string;
  createdAt: number;
  lastConnectedAt?: number;
  online: boolean;
  deviceCount: number;
}

export const authAdminApi = {
  login(username: string, password: string): Promise<{
    ok: true;
    user: { username: string; role: 'admin'; adminUsername: string };
    expiresAt: number;
  }> {
    return jsonFetch(buildUrl('/api/auth/admin-login'), {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
  logout(): Promise<{ ok: true }> {
    return jsonFetch(buildUrl('/api/auth/admin-logout'), { method: 'POST' });
  },
};

export const adminApi = {
  listUsers(): Promise<{ users: AdminUser[] }> {
    return jsonFetch(buildUrl('/api/admin/users'));
  },
  upsertUser(username: string, password: string): Promise<{ user: AdminUser }> {
    return jsonFetch(buildUrl('/api/admin/users'), {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
  deleteUser(username: string): Promise<{ ok: true }> {
    return jsonFetch(buildUrl(`/api/admin/users/${encodeURIComponent(username)}`), {
      method: 'DELETE',
    });
  },
  listDevices(): Promise<{ devices: DeviceRecord[] }> {
    return jsonFetch(buildUrl('/api/admin/devices'));
  },
  listAgents(): Promise<{ agents: AdminAgentSummary[] }> {
    return jsonFetch(buildUrl('/api/admin/agents'));
  },
};

/* ---------------- Local-service process control ---------------- */
/**
 * Talks to the per-machine adh helper process at `127.0.0.1:10987` (or
 * wherever the user pointed it). Distinct from {@link adminApi}, which
 * is the *hub-level* user/device admin surface — this object is about
 * the OS-level lifecycle of the helper binary itself: see status, hot
 * restart, graceful stop, edit + persist the agent-local.json overlay.
 *
 * Backed by `/api/admin/{process,restart,shutdown,config}` in
 * src/server/httpApi.ts. Auth-open during the dev phase (see plan's
 * "上线前 must-do" for the production hardening to-do).
 */
export interface LocalServiceProcessInfo {
  app: 'android-device-hub';
  pid: number;
  version: string;
  /** Unix-ms timestamp of when the current process module loaded. */
  startedAt: number;
  uptimeMs: number;
  nodeVersion: string;
  platform: NodeJS.Platform | string;
  arch: string;
  dataDir: string;
  logPath: string;
  configFile: string | null;
  host: string;
  port: number | null;
  execPath: string;
}

export const localServiceApi = {
  process(): Promise<LocalServiceProcessInfo> {
    return jsonFetch(buildUrl('/api/admin/process'));
  },
  restart(): Promise<{ ok: true; action: 'restart'; etaMs: number }> {
    return jsonFetch(buildUrl('/api/admin/restart'), { method: 'POST' });
  },
  shutdown(): Promise<{ ok: true; action: 'shutdown' }> {
    return jsonFetch(buildUrl('/api/admin/shutdown'), { method: 'POST' });
  },
  // The `patch` shape must match the whitelist in src/server/httpApi.ts.
  // We accept a loose `Record<string, unknown>` here so callers (the
  // LocalService settings form) can build partial-and-typed payloads
  // through the existing AgentLocalConfig zustand store without
  // re-declaring every nested key.
  updateConfig<T extends Record<string, unknown>>(
    patch: T,
  ): Promise<{ ok: true; needsRestart: boolean; config: unknown; configFile: string | null }> {
    return jsonFetch(buildUrl('/api/admin/config'), {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },
};

/* ---------------- WebSocket helpers ---------------- */
export function buildWsUrl(path: string, params?: Record<string, string | number | undefined>): string {
  // Same-origin (PWA / vite dev): use the page's own host so wss/ws picks
  // the right scheme automatically.
  //
  // Cross-origin (public hub → local agent): derive the ws origin from
  // API_BASE (e.g. `http://127.0.0.1:10987` → `ws://127.0.0.1:10987`).
  // Browsers (Chrome 94+/Edge/Firefox/Safari 17+) treat `ws://localhost`
  // and `ws://127.0.0.1` as exemptions from the HTTPS-page mixed-content
  // block, so this works from a public HTTPS hub origin without extra
  // configuration on the local side.
  let proto: string;
  let host: string;
  if (IS_SAME_ORIGIN) {
    proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    host = window.location.host;
  } else {
    const apiUrl = new URL(API_BASE);
    proto = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    host = apiUrl.host;
  }
  const url = new URL(`${proto}//${host}${path}`);
  const token = getToken();
  if (token) url.searchParams.set('token', token);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/* ---------------- Stage B.6 — runtime info ---------------- */

export interface RuntimeAgentInfo {
  agentId: string;
  name: string | null;
  hostname: string | null;
  os: string | null;
  online: boolean;
  deviceCount: number;
  protocolVersion: number | null;
  agentVersion: string | null;
  capabilities: string[];
  /** Optional LAN port advertised by the agent for direct mirror WS. */
  lanPort?: number | null;
  /** Optional LAN host advertised by the agent. Default 127.0.0.1. */
  lanHost?: string | null;
}

export interface RuntimeInfo {
  app: string;
  version: string;
  mode: 'hub-only' | 'agent-hub' | 'standalone';
  protocol: 'http' | 'https';
  features: {
    multiUser: boolean;
    sharedDevices: boolean;
    auth: boolean;
    usageEvents: boolean;
  };
  agents: { online: number; total: number; list: RuntimeAgentInfo[] };
  service: { enabled: boolean };
  loadedAt: number;
}

export const runtimeInfoApi = {
  get(): Promise<RuntimeInfo> {
    return jsonFetch(buildUrl('/api/runtime-info'));
  },
};

/* ---------------- Stage B.4 — mirror endpoint router ---------------- */

/**
 * Decide where the SPA's mirror / scrcpy WebSocket should connect.
 *
 * Routing matrix (simplified for B.4 MVP):
 *
 *   1. Hub is on https:// (e.g. publicly hosted) → always tunnel through
 *      the hub's `/api/screen/stream`. Even local agents go this route
 *      because mixed-content rules forbid `ws://` from a `https://` page.
 *
 *   2. Hub is on http:// AND device is owned (kind=own) AND its agent
 *      is online AND the agent advertised a LAN port → connect direct
 *      to the agent's `ws://<lanHost>:<lanPort>/api/screen/stream` —
 *      avoids the hub being a bandwidth bottleneck for the user's own
 *      USB phone.
 *
 *   3. Otherwise (shared device, agent offline, mixed content, missing
 *      LAN info) → fall back to hub tunnel.
 *
 * `kind` and the agent metadata come from the device row +
 * `runtime-info`. The caller (MirrorCanvas) is the one that knows
 * those values; this helper just glues them.
 */
export interface MirrorRouteHints {
  /** Device ownership classifier. `'own'` = USB / wireless connected
   *  on the same machine the SPA is running on (i.e. the local agent
   *  reports it). `'shared'` = remote device exposed via another
   *  user's agent. */
  kind: 'own' | 'shared' | 'unknown';
  /** When `kind=own`: which agent owns this device. */
  agentId?: string;
  /** Hub-published agents snapshot. */
  agents?: RuntimeAgentInfo[];
}

export interface MirrorRouteDecision {
  url: string;
  /** `'agent'` = direct LAN; `'hub'` = tunneled through hub's /api/screen/stream. */
  via: 'agent' | 'hub';
  reason: string;
}

export function buildMirrorWsUrl(
  serial: string,
  hints: MirrorRouteHints,
  params?: Record<string, string | number | undefined>
): MirrorRouteDecision {
  const hubProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const hubUrl = (() => {
    const url = new URL(`${hubProto}//${window.location.host}/api/screen/stream`);
    url.searchParams.set('serial', serial);
    const token = getToken();
    if (token) url.searchParams.set('token', token);
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined || v === null) continue;
        url.searchParams.set(k, String(v));
      }
    }
    return url.toString();
  })();

  // Mixed-content rule: https:// page can NEVER open ws:// to
  // 127.0.0.1, even though the browser shows zero security warning
  // for the rest of the page. Tunneling through the hub stays wss://
  // so it passes.
  if (window.location.protocol === 'https:') {
    return { url: hubUrl, via: 'hub', reason: 'mixed-content' };
  }

  if (hints.kind === 'own' && hints.agentId && hints.agents) {
    const agent = hints.agents.find((a) => a.agentId === hints.agentId);
    if (agent && agent.online && agent.lanPort) {
      const host = agent.lanHost ?? '127.0.0.1';
      const url = new URL(`ws://${host}:${agent.lanPort}/api/screen/stream`);
      url.searchParams.set('serial', serial);
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          if (v === undefined || v === null) continue;
          url.searchParams.set(k, String(v));
        }
      }
      return { url: url.toString(), via: 'agent', reason: 'own-device-direct' };
    }
  }

  return {
    url: hubUrl,
    via: 'hub',
    reason: hints.kind === 'shared' ? 'shared-tunnel' : 'fallback',
  };
}
